import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/alerts/api/config.ts";
import { getAlertById } from "@/alerts/api/requests/machine-alerts.requests.ts";
import type { AlertDetail } from "@/alerts/types/machine-alert.ts";
import type { ApiResponse } from "@/shared/types/api.ts";
import { useMachineAlerts } from "@/alerts/hooks/useMachineAlerts";
import { labelConfidencePercent } from "@/shared/utils/confidence";

const MAX_IMAGES = 8;

export interface MachineImage {
  url: string;
  /** The alert this snapshot belongs to — clicking opens its detail. */
  alertId: number;
  timestamp: string;
}

/**
 * Snapshot images for a machine — pulls the latest alerts and resolves each
 * one's detail (`getAlertById`) to collect the alert-frame signed URLs (the
 * highest-confidence image, matching MapEventCard's default).
 * Each image keeps its source `alertId` so the Images panel can open the same
 * alert-detail card. Queries only run while the consumer (Images panel) is
 * mounted.
 */
export const useMachineImages = (
  deviceMxid?: string,
): { images: MachineImage[]; isLoading: boolean } => {
  const alerts = useMachineAlerts(deviceMxid);
  const ids = useMemo(() => alerts.slice(0, MAX_IMAGES).map((a) => a.id), [alerts]);

  const queries = useQueries({
    queries: ids.map((id) => ({
      // Same canonical (string) key `useGetAlertById` builds — otherwise the
      // alert card and this panel fetch the same detail twice.
      queryKey: [QUERY_KEYS.ALERTS, String(id)],
      queryFn: (): Promise<ApiResponse<AlertDetail>> => getAlertById(id),
      enabled: !!id,
    })),
  });

  return useMemo(() => {
    const images: MachineImage[] = [];
    let isLoading = false;
    queries.forEach((q) => {
      if (q.status === "pending") isLoading = true;
      const d = q.data?.data;
      const detailImages = d?.images ?? [];
      // The alert frame — highest-confidence image; preview only as fallback.
      const frame = detailImages.length
        ? detailImages.reduce((best, im) =>
            (labelConfidencePercent(im.labels) ?? -1) > (labelConfidencePercent(best.labels) ?? -1) ? im : best,
          )
        : d?.preview;
      if (d && frame?.signed_url) {
        images.push({ url: frame.signed_url, alertId: d.id, timestamp: frame.timestamp });
      }
    });
    return { images, isLoading };
  }, [queries]);
};
